import {
  ChatAttachmentApiError,
  confirmChatAttachmentUpload,
  fetchChatAttachmentDownload,
  startChatAttachmentUpload,
  uploadChatAttachmentBytes
} from '../../../services/chatAttachments';
import { ParticipantRole } from '../../../utils/participantRole';
import { createEicarFixture, createPhotoFixture } from './chatAttachmentFixtures';

type ChatSession = ReturnType<typeof connect.ChatSession.create>;
type ScanStatus = connect.AttachmentItem['Status'];

interface ChatCredentials {
  accessToken: string;
  connectionToken: string;
  contactId: string;
}

interface HarnessResult {
  file: string;
  verdict: ScanStatus;
  download: string;
}

const PARTICIPANT_JOINED_EVENT = 'application/vnd.amazonaws.connect.event.participant.joined';
const AGENT_JOIN_TIMEOUT_MS = 10 * 60_000;
const SCAN_VERDICT_TIMEOUT_MS = 60_000;
const VERDICT_POLL_INTERVAL_MS = 500;

const getConnectionToken = (session: ChatSession): string => {
  const token = session.getChatDetails().connectionDetails?.connectionToken;
  if (!token) throw new Error('Chat session has no connection token; connect() must finish first');

  return token;
};

const waitForAgentToJoin = (session: ChatSession): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Timed out waiting for an agent to join')), AGENT_JOIN_TIMEOUT_MS);

    session.onMessage(({ data }) => {
      if (data.ContentType === PARTICIPANT_JOINED_EVENT && data.ParticipantRole === ParticipantRole.AGENT) {
        clearTimeout(timer);
        resolve();
      }
    });
  });

// Connect sends one transcript item per attachment, already carrying the final scan status.
const trackVerdicts = (session: ChatSession): Map<string, ScanStatus> => {
  const verdicts = new Map<string, ScanStatus>();

  session.onMessage(({ data }) => {
    data.Attachments?.forEach(({ AttachmentId, Status }) => verdicts.set(AttachmentId, Status));
  });

  return verdicts;
};

const waitForVerdict = async (verdicts: Map<string, ScanStatus>, attachmentId: string): Promise<ScanStatus> => {
  const deadline = Date.now() + SCAN_VERDICT_TIMEOUT_MS;

  while (Date.now() < deadline) {
    const verdict = verdicts.get(attachmentId);
    if (verdict) return verdict;

    await new Promise(resolve => setTimeout(resolve, VERDICT_POLL_INTERVAL_MS));
  }

  throw new Error(`No transcript verdict for attachment ${attachmentId} within ${SCAN_VERDICT_TIMEOUT_MS / 1000}s`);
};

const uploadAttachment = async (
  { accessToken, connectionToken, contactId }: ChatCredentials,
  file: File
): Promise<string> => {
  const { attachmentId, upload } = await startChatAttachmentUpload(accessToken, connectionToken, contactId, file);
  await uploadChatAttachmentBytes(upload, file);
  // Confirm right after the upload: Connect can fail a file unscanned if the confirmation lags behind.
  await confirmChatAttachmentUpload(accessToken, connectionToken, contactId, attachmentId);

  return attachmentId;
};

const describeDownload = async (
  { accessToken, connectionToken, contactId }: ChatCredentials,
  attachmentId: string
): Promise<string> => {
  try {
    await fetchChatAttachmentDownload(accessToken, connectionToken, contactId, attachmentId);
    return '200 download link';
  } catch (error) {
    if (error instanceof ChatAttachmentApiError) return `${error.status} ${error.code ?? ''}`.trim();

    // A missing route answers 404 without CORS headers, which the browser reports as a network error.
    return 'no response (route missing or blocked by CORS)';
  }
};

/**
 * Plays the customer on a chat: once an agent has joined, uploads a photo and an EICAR test file through our
 * attachment endpoints, then logs each file's scan verdict and what the download endpoint answers for it.
 */
export const runChatAttachmentHarness = async (
  session: ChatSession,
  contactId: string,
  accessToken: string
): Promise<void> => {
  const credentials: ChatCredentials = { accessToken, connectionToken: getConnectionToken(session), contactId };
  const agentJoined = waitForAgentToJoin(session);
  const verdicts = trackVerdicts(session);

  console.info(`[chat-attachment-harness] ${contactId}: waiting for an agent to join`);
  await agentJoined;

  const results: HarnessResult[] = [];

  for (const file of [await createPhotoFixture(), createEicarFixture()]) {
    const attachmentId = await uploadAttachment(credentials, file);
    const verdict = await waitForVerdict(verdicts, attachmentId);
    const download = await describeDownload(credentials, attachmentId);

    results.push({ file: file.name, verdict, download });
  }

  console.table(results);
};
